<template>
  <div class="lesson-text-box relative">
    <VLessonPlaceholder v-if="isEmptyBlock" />
    
    <div v-else
      class="lesson-container"
      :style="[
        StyleUtils.backgroundWithPattern(
          fields.mainGroup?.backgroundColor,
          fields.mainGroup?.isShowBackgroundColor,
          fields.mainGroup?.backgroundPattern,
          fields.mainGroup?.backgroundPattern?.enabled
        ),
        CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
        CssStyleHelpers.margins(fields.mainGroup?.margins),
      ]"
    >
      <div
        class="w-full h-full flex main-group"
        :style="[
          CssStyleHelpers.paddings(fields.mainGroup?.paddings),
          {
            '--main-padding-top-mobile': `${fields.mainGroup?.paddingsMobile?.top ?? fields.mainGroup?.paddings?.top ?? 0}px`,
            '--main-padding-right-mobile': `${fields.mainGroup?.paddingsMobile?.right ?? fields.mainGroup?.paddings?.right ?? 0}px`,
            '--main-padding-bottom-mobile': `${fields.mainGroup?.paddingsMobile?.bottom ?? fields.mainGroup?.paddings?.bottom ?? 0}px`,
            '--main-padding-left-mobile': `${fields.mainGroup?.paddingsMobile?.left ?? fields.mainGroup?.paddings?.left ?? 0}px`,
          },
        ]"
      >
        <!-- Медиа контент -->
        <div
          v-if="fields.mainGroup?.isShowBackgroundMedia && (fields.mainGroup?.backgroundMedia?.previewUrl || fields.mainGroup?.backgroundMedia?.url)"
          class="media-background w-full overflow-hidden"
          :style="[
            CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
          ]"
        >
          <MediaCard
            fill
            :media-type="fields.mainGroup?.backgroundMedia?.type"
            :image-props="CssImageHelpers.imageProps(fields.mainGroup?.backgroundMedia)"
            :video-props="fields.mainGroup?.videoProps"
            :mask-props="fields.mainGroup?.backgroundMediaMask"
            :object-fit="fields.mainGroup?.objectFit || 'cover'"
          />
        </div>

        <!-- Карточки -->
        <!-- DEBUG: розкоментуй щоб побачити стан даних
        <pre style="font-size:11px;background:#fff8;color:#000;padding:8px;border-radius:6px;white-space:pre-wrap;word-break:break-all;z-index:9999;position:relative">
cards count: {{ fields.cardsGroup?.cardsCollection?.length }}
maxImageHeight: {{ fields.cardsGroup?.maxImageHeight }}
isAllOverlay: {{ isAllOverlayCards }}
minHeight: {{ overlayCardsMinHeight }}
layouts: {{ fields.cardsGroup?.cardsCollection?.map(c => c?.layoutField?.data?.value) }}
card[0].mediaField.data: {{ JSON.stringify(fields.cardsGroup?.cardsCollection?.[0]?.mediaField?.data?.containerPreset ?? fields.cardsGroup?.cardsCollection?.[0]?.mediaField?.data?.containerHeight) }}
        </pre>
        -->
        <div
          v-if="fields.cardsGroup?.cardsCollection?.length > 0"
          class="overflow-hidden grid cards-grid"
          :class="{ 'cards-grid--all-overlay': isAllOverlayCards }"
          :style="[
            CssStyleHelpers.gap(fields.cardsGroup?.cardsBetweenSpacing || 0),
            {
              '--cards-count': fields.cardsGroup?.cardsCollection?.length,
              '--cards-min-height': `${overlayCardsMinHeight.value}px`,
            }
          ]"
        >
          <template v-for="(card, index) in fields.cardsGroup?.cardsCollection" :key="index">
            <VerticalCard
              v-if="card?.layoutField?.data?.value !== 'text-overlay'"
              :card="card"
              :cards-group="fields.cardsGroup"
              :main-group="fields.mainGroup"
              :max-image-height="fields.cardsGroup?.maxImageHeight"
            />
            <OverlayCard
              v-else
              :card="card"
              :cards-group="fields.cardsGroup"
              :main-group="fields.mainGroup"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, PropType } from "vue";
import MediaCard from "../cards/MediaCard.vue";
import VerticalCard from "../cards/VerticalCard.vue";
import OverlayCard from "../cards/OverlayCard.vue";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
import { ILayoutComponentData } from "../types/ILayoutFieldData";
import { CssImageHelpers, CssStyleHelpers, CssVideoHelpers, StyleUtils } from "../utils/cssStyleHelpers";
import { useDataFields } from "../composables/data";
import { getFieldFromData } from "../composables/data/dataFieldHelper";
const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
});

// Використовуємо дані напряму з newData
const applyDefaultMainPaddings = (paddings?: Record<string, number>) => ({
  top: paddings?.top ?? 0,
  right: paddings?.right ?? 0,
  bottom: paddings?.bottom ?? 0,
  left: paddings?.left ?? 0,
  ...paddings,
});

const applyDefaultMainMargins = (margins?: Record<string, number>) => ({
  top: margins?.top ?? 50,
  right: margins?.right ?? 0,
  bottom: margins?.bottom ?? 0,
  left: margins?.left ?? 0,
  ...margins,
});

const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData) return { cardsGroup: {} };

  const { getData } = useDataFields(newData);

  const mainBackgroundMedia = getData("mainGroup.backgroundMedia");
  
  // Отримуємо поле для доступу до type на рівні поля
  const backgroundMediaField = getFieldFromData("mainGroup.backgroundMedia", newData);
  const mediaType = backgroundMediaField?.field?.type || mainBackgroundMedia?.type;
  
  // Перевіряємо чи є дані для відображення медіа
  const hasMediaData = mainBackgroundMedia && (
    mainBackgroundMedia.previewUrl || 
    mainBackgroundMedia.url ||
    (mediaType && (mediaType === 'image' || mediaType === 'video'))
  );

  // Об'єднуємо дані з type на рівні поля
  const backgroundMediaWithType = mainBackgroundMedia ? {
    ...mainBackgroundMedia,
    type: mediaType || mainBackgroundMedia.type
  } : null;

  return {
    mainGroup: {
      isShowCardMedia: getData("mainGroup.isShowCardMedia")?.value,
      isShowCardText: getData("mainGroup.isShowCardText")?.value,
      isShowBackgroundColor: getData("mainGroup.isShowBackgroundColor")?.value,
      isShowBackgroundMedia: getData("mainGroup.isShowBackgroundMedia")?.value && hasMediaData,
      backgroundColor: getData("mainGroup.backgroundColor")?.value,
      borderRadius: getData("mainGroup.borderRadius")?.value,
      backgroundMedia: backgroundMediaWithType,
      objectFit: mainBackgroundMedia?.objectFit ?? 'cover',
      videoProps: {
        ...CssVideoHelpers.videoProps(mainBackgroundMedia),
        autoplay: mainBackgroundMedia?.autoplay,
        muted: mainBackgroundMedia?.muted,
        loop: mainBackgroundMedia?.loop,
      },
      backgroundPattern: getData("mainGroup.backgroundPattern"),
      backgroundMediaMask: getData("mainGroup.backgroundMediaMask"),
      paddings: applyDefaultMainPaddings(getData("mainGroup.paddings")),
      paddingsMobile: applyDefaultMainPaddings(
        getData("mainGroup.paddingsMobile") || getData("mainGroup.paddings")
      ),
      margins: applyDefaultMainMargins(getData("mainGroup.margins")),
    },
    // Все данные и стили для карточек
    cardsGroup: {
      // Основные данные карточек
      cardsCollection: getData("cardsGroup.cards")?.cardsCollection,
      maxImageHeight: getData("cardsGroup.cards")?.maxImageHeight,

      // Стили контейнера карточек (внешние отступы, размытие, фон)
      borderRadius: getData("cardsGroup.cardsBorderRadius")?.value,
      cardsBetweenSpacing: getData("cardsGroup.cardsBetweenSpacing")?.value,
      cardTextSpacing: getData("cardsGroup.cardTextSpacing"),
      cardMediaTextSpacing: getData("cardsGroup.cardMediaTextSpacing")?.value,
      cardsBorderRadius: getData("cardsGroup.cardsBorderRadius")?.value,
      textBorderRadius: getData("cardsGroup.textBorderRadius")?.value,
      imageBorderRadius: getData("cardsGroup.imageBorderRadius")?.value,
      // Стили контента карточек (внутренний фон) - тепер індивідуальні для кожної картки
      isTextBackgroundShown: getData("cardsGroup.isShowCardText")?.value,
      textBackgroundColor: getData("cardsGroup.textBackgroundColor")?.value,

      // Поля відображення передаємо з mainGroup для сумісності з VerticalCard
      isShowCardMedia: getData("mainGroup.isShowCardMedia")?.value,
      isShowTextMedia: getData("mainGroup.isShowCardText")?.value,
      isShowBackgroundMedia: getData("mainGroup.isShowBackgroundMedia")?.value,

      // Настройки медиа
      imageProps: CssImageHelpers.imageProps(getData("mainGroup.backgroundMedia")),
      videoProps: CssVideoHelpers.videoProps(getData("mainGroup.backgroundMedia")),
      maskProps: getData("mainGroup.backgroundMediaMask"),
    },
  };
});

const isEditMode = computed(() => props.mode === "edit");

// Коли ВСІ картки — overlay (text-overlay), їх контент повністю поза потоком
// (media = position:absolute, content = min-height), тому auto grid-row колапсує до 0.
// У цьому випадку задаємо grid-track мінімальну висоту явно.
const isAllOverlayCards = computed(() => {
  const cards = fields.value?.cardsGroup?.cardsCollection;
  if (!cards?.length) return false;
  return cards.every(
    (card: any) => card?.layoutField?.data?.value === "text-overlay"
  );
});

// Max preset height across all cards — береться з containerPreset.height кожної картки.
// Fallback: maxImageHeight (глобальне), потім 300.
const overlayCardsMinHeight = computed(() => {
  const cards = fields.value?.cardsGroup?.cardsCollection as any[] | undefined;
  if (cards?.length) {
    const heights = cards.map((card: any) => {
      const d = card?.mediaField?.data;
      return Number(d?.containerPreset?.height ?? d?.containerHeight ?? d?.height ?? 0);
    }).filter((h: number) => h > 0);
    if (heights.length) return Math.max(...heights);
  }
  return fields.value?.cardsGroup?.maxImageHeight || 300;
});

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasCards = fields.value?.cardsGroup?.cardsCollection && fields.value.cardsGroup.cardsCollection.length > 0;
  // Проверяем медиа - isShowBackgroundMedia уже учитывает наличие данных
  const hasMedia = fields.value?.mainGroup?.isShowBackgroundMedia === true;
  const hasColor = fields.value?.mainGroup?.isShowBackgroundColor && fields.value?.mainGroup?.backgroundColor;
  const hasPattern = fields.value?.mainGroup?.backgroundPattern && fields.value?.mainGroup?.backgroundPattern?.enabled;
  
  return !(hasCards || hasMedia || hasColor || hasPattern);
});
</script>
<style scoped>
@reference "tailwindcss";
/* Основные стили для контейнера */
.lesson-text-box {
  @apply w-full flex flex-col gap-0;
  container-type: inline-size;
}

/* Стили для контейнера с патерном */
.lesson-container {
  width: 100%;
  height: 100%;
  margin: 0;
  box-sizing: border-box;
  transition: all 0.3s ease;
  position: relative;
  /* Дозволяємо патерну працювати коректно - важливо! */
  background-attachment: local !important;
  background-origin: border-box !important;
  background-clip: border-box !important;
  /* Забезпечуємо правильне позиціонування патерну */
  background-position: 0% 0% !important;
  background-repeat: repeat !important;
  /* Переконуємося що патерн буде видимий */
  /* min-height: 200px; */
  /* Додаткові стилі для патерну */
  background-color: transparent;
  z-index: 1;
}

/* Переконуємося що патерн відображається поверх інших елементів */
.lesson-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  pointer-events: none;
}

/* Стили для заголовка */
.lesson-title {
  @apply mb-4 relative z-10;
  color: var(--text-primary);
  /* background: rgba(255, 255, 255, 0.9); */
  /* padding: 0.75rem 1rem; */
  border-radius: 0.5rem;
  /* backdrop-filter: blur(2px); */
  display: inline-block;
}

/* Стили для текста */
.lesson-text {
  @apply text-base leading-relaxed m-0 p-0 mb-6;
  color: var(--text-primary);
}

/* Стили для медиа фона */
.media-background {
  @apply w-full h-full absolute inset-0 z-0;
  border-radius: inherit;
  overflow: hidden;
}
.media-background :deep(.media-card) {
  height: 100%;
}

/* Override @container height:auto !important from MediaContainerLayer and MaskLayer —
   those resets are correct for inline media but must not apply to the background fill. */
.media-background :deep(.media-container-layer),
.media-background :deep(.media-container-layer > div) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  aspect-ratio: unset !important;
}

.media-background :deep(img),
.media-background :deep(video) {
  @apply w-full h-full object-cover;
}

/* Стили для текста поверх медиа */
.card-item {
  @apply text-base leading-relaxed relative z-10;
  /* color: var(--text-primary); */
  /* background: rgba(255, 255, 255, 0.2); */
}

/* Стили для заглушки */


.placeholder-icon {
  @apply text-5xl text-gray-500 mb-4 opacity-70;
}

.placeholder-text h3 {
  @apply text-lg font-semibold text-gray-700 m-0 mb-2;
}

.placeholder-text p {
  @apply text-sm text-gray-500 m-0 leading-relaxed;
}

/* Адаптивность для мобильных устройств */
@container (max-width: 700px) {
  .main-group {
    padding-top: var(--main-padding-top-mobile) !important;
    padding-right: var(--main-padding-right-mobile) !important;
    padding-bottom: var(--main-padding-bottom-mobile) !important;
    padding-left: var(--main-padding-left-mobile) !important;
  }

  .lesson-text {
    @apply text-base;
  }

  .lesson-title {
    @apply text-base;
  }
}

/* Стилі для заглушки */


.placeholder-icon {
  font-size: 48px;
  color: #718096;
  margin-bottom: 16px;
  opacity: 0.7;
}

.placeholder-text h3 {
  font-size: 18px;
  font-weight: 600;
  color: #4a5568;
  margin: 0 0 8px 0;
}

.placeholder-text p {
  font-size: 14px;
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(var(--cards-count), 1fr);
  align-items: stretch; /* Выравнивание карточек по высоте */
}

/* Коли ВСІ картки overlay — їх вміст поза потоком (absolute media + stretched content),
   тому auto grid-row обраховується як 0 і рядок колапсує.
   Задаємо grid-track мінімальну висоту явно, щоб рядок отримав реальну висоту,
   від якої height:100% дочірніх карток стане ненульовим. */
.cards-grid--all-overlay {
  grid-auto-rows: minmax(var(--cards-min-height, 300px), auto);
}

/* Для десктопа: выравнивание медиа-карточек по максимальной высоте */
@container (min-width: 720px) {
  .cards-grid {
    align-items: stretch;
    grid-auto-rows: minmax(300px, auto);
  }
  
  /* Пробрасываем высоту через всю цепочку контейнеров */
  .cards-grid > * {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  
  /* Медиа-секция в каждой карточке растягивается одинаково */
  .cards-grid :deep(.vertical-card-media) {
    flex: 1 1 auto;
    min-height: 0; /* Позволяет flex-элементу сжиматься */
    /* aspect-ratio сохраняется через inline style в VerticalCard */
    /* Это позволяет каждой картинке сохранить свои пропорции */
    /* при этом все контейнеры будут одинаковой высоты */
  }
}

/* Мобільна адаптація для сітки карток */
@container (max-width: 700px) {
  .cards-grid {
    grid-template-columns: none;
    align-items: start; /* На мобилке карточки не выравниваются */
    /* grid-template-rows: repeat(var(--cards-count), 1fr); */
    width: 100%;
  }
  
  .cards-grid > * {
    height: auto;
  }
  
  .cards-grid :deep(.vertical-card-media) {
    flex: none;
  }
}
</style>
<style>
@import "../styles/dialogs-styles.css";
</style>
